/**
 * 패널이 부르는 `window.__elmanAgentAsk` 를 만든다.
 *
 * 패널은 UI 만 알고 런타임은 브라우저만 안다. 둘을 잇는 것이 이 파일 하나뿐이라,
 * 패널을 지워도 런타임이 남고 런타임을 지워도 패널이 "로드되지 않았습니다" 로 산다.
 */

import { AgentBridge, Session } from './driver';
import {
  AgentScreen, AgentSkill, CodeLists, Concepts, Expert, Procedures, TaskSpec, ask, createSandbox,
} from './runtime';
import {
  makeAliases, resetSpend, makeChat, makeChatStream, makeExperiencePromote, makeShortlist,
} from './client';
import type { AgentAsk, AgentProgress } from '../app/components/common/AgentPanel/types';

/** 화면 카탈로그는 230KB 다. 번들에 넣지 않고 첫 질문 때 받는다. */
const SCREENS_URL = '/agent-screens.json';
/** 화면별 조작 절차. "어떻게 하나요" 에 답하는 데 쓴다. 없어도 조회는 그대로 된다. */
const PROCEDURES_URL = '/agent-procedures.json';
const SKILLS_URL = '/agent-skills.json';
/** 코드 칸이 고르는 목록. 없으면 지어낸 코드값을 못 거를 뿐 조회·작성은 그대로 된다. */
const CODELISTS_URL = '/agent-codelists.json';
/** 사람이 말한 낱말 → 열의 뜻. 없으면 예전처럼 낱말 표만 쓴다. */
const CONCEPTS_URL = '/agent-concepts.json';
/** 업무 분야 전문가 — 용어·코드 뜻·분석 틀. 없으면 예전처럼 돈다. */
const EXPERTS_URL = '/agent-experts.json';
/** 서버 문제가 생기면 환경변수 하나로 기존 라우팅으로 되돌릴 수 있다. */
const AGENT_V2 = process.env.REACT_APP_AGENT_V2 !== 'false';

let screens: Promise<AgentScreen[]> | null = null;
let procedures: Promise<Procedures> | null = null;
let skills: Promise<AgentSkill[]> | null = null;
let codeLists: Promise<CodeLists> | null = null;
let concepts: Promise<Concepts> | null = null;
let experts: Promise<Expert[]> | null = null;

function loadScreens(): Promise<AgentScreen[]> {
  if (!screens) {
    screens = fetch(SCREENS_URL)
      .then((response) => {
        if (!response.ok) throw new Error(`${SCREENS_URL}: HTTP ${response.status}`);
        return response.json();
      })
      .catch((error) => {
        // 다음 질문에서 다시 받아 볼 수 있게 실패를 기억하지 않는다.
        screens = null;
        throw error;
      });
  }
  return screens;
}

/**
 * 절차는 없어도 된다. 못 받으면 조회는 그대로 되고 "어떻게 하나요" 만 못 답한다 —
 * 그것 때문에 질문 전체를 죽일 이유가 없다.
 */
function loadProcedures(): Promise<Procedures> {
  if (!procedures) {
    procedures = fetch(PROCEDURES_URL)
      .then((response) => (response.ok ? response.json() : {}))
      .catch(() => ({}));
  }
  return procedures;
}

/**
 * 절차와 같은 이유로 없어도 된다. 못 받으면 지어낸 코드값을 못 거를 뿐이고, 그것 때문에
 * 질문 전체를 죽일 이유가 없다.
 */
function loadCodeLists(): Promise<CodeLists> {
  if (!codeLists) {
    codeLists = fetch(CODELISTS_URL)
      .then((response) => (response.ok ? response.json() : {}))
      .catch(() => ({}));
  }
  return codeLists;
}

function loadConcepts(): Promise<Concepts> {
  if (!concepts) {
    concepts = fetch(CONCEPTS_URL)
      .then((response) => (response.ok ? response.json() : {}))
      .catch(() => ({}));
  }
  return concepts;
}

function loadExperts(): Promise<Expert[]> {
  if (!experts) {
    experts = fetch(EXPERTS_URL)
      .then((response) => (response.ok ? response.json() : []))
      .catch(() => []);
  }
  return experts;
}

function loadSkills(): Promise<AgentSkill[]> {
  if (!skills) {
    skills = fetch(SKILLS_URL)
      .then((response) => (response.ok ? response.json() : []))
      .catch(() => []);
  }
  return skills;
}

function bridge(): AgentBridge | null {
  return (window as unknown as { __elmanAgent?: AgentBridge }).__elmanAgent ?? null;
}

/**
 * 다음 질문의 후보로 되살릴 화면 경로.
 *
 * 예전에는 `sources` 앞부분을 잘라 썼다. 그 문자열이 사람이 읽는 화면 이름으로 바뀌면
 * 조용히 이름을 경로로 넘기게 된다 — 런타임이 경로를 따로 주면 그것을 쓴다.
 */
function plan_routes(answer: { routes?: string[]; sources: string[] }): string[] {
  if (answer.routes?.length) return answer.routes.filter(Boolean);
  return answer.sources.map((source) => source.split(' ')[0]).filter((source) => source.startsWith('/'));
}

export type Caller = () => { custcd: string; perid: string; token: string } | null;

export function installAgent(caller: Caller): void {
  const run: AgentAsk = async (
    question, onProgress, signal, history, experiences, auto, aliases, files,
  ) => {
    const who = caller();
    if (!who) throw new Error('로그인 정보가 없습니다.');

    const [catalog, howto, capabilities, lists, meanings, specialists] = await Promise.all([
      loadScreens(), loadProcedures(), loadSkills(), loadCodeLists(), loadConcepts(), loadExperts(),
    ]);
    // 워커는 CSP 헤더가 붙어 있을 때만 만들어진다. 없으면 여기서 이유를 말하며 죽는다.
    const sandbox = await createSandbox();
    const app = bridge();
    // 이 턴에 LLM 을 몇 번·얼마나 부르는지 센다. 답이 끝나면 `[agent] 씀` 의 마지막
    // 줄이 이 턴의 총계다 — 줄일 자리를 그 숫자로 고른다.
    resetSpend();
    const chat = makeChat(who, signal);
    // 되묻기나 일시 오류 메시지에 task가 없어도 그 앞의 업무 상태를 잃지 않는다.
    const currentTask: TaskSpec | undefined = [...(history ?? [])].reverse()
      .map((message) => message.task ?? message.result?.task)
      .find((candidate): candidate is TaskSpec => Boolean(candidate));
    const mergedExperiences = [...(experiences ?? [])];
    const receiveExperiences = (shared: typeof mergedExperiences) => {
      for (const item of shared) {
        if (!mergedExperiences.some((other) => (
          other.question === item.question && JSON.stringify(other.recipe) === JSON.stringify(item.recipe)
        ))) mergedExperiences.push(item);
      }
    };

    try {
      const answer = await ask(question, {
        screens: catalog,
        procedures: howto,
        skills: capabilities,
        codeLists: lists,
        concepts: meanings,
        experts: specialists,
        session: new Session(),
        sandbox,
        chat,
        taskChat: AGENT_V2 ? chat : undefined,
        chatStream: makeChatStream(who, signal),
        shortlist: makeShortlist(who, signal, receiveExperiences),
        context: {
          currentRoute: app?.currentPath() ?? undefined,
          openTabs: app?.openTabs?.() ?? undefined,
          me: (app as { me?: () => unknown })?.me?.() ?? undefined,
          businessSites: app?.businessSites?.() ?? undefined,
          businessSiteContext: (app as { currentBusinessSite?: () => string | null })
            ?.currentBusinessSite?.() ?? undefined,
          experiences: mergedExperiences,
          // 사람이 「맞아요」를 눌러 검증된 레시피가 있으면 그 화면을 모델에 다시 묻지
          // 않고 그대로 쓴다(스펙 §5.6). 같은 지시에 판마다 다른 화면으로 가던 것을
          // 끊는다. 레시피가 없으면 지금과 똑같이 모델이 고른다 — 켜도 손해가 없다.
          confirmRecipes: true,
          history,
          task: currentTask,
          // 사용자가 패널에서 체크한 값 그대로다. 모델이 바꿀 수 없다.
          auto: Boolean(auto),
          aliases,
          // 이번 턴에 올린 파일만 싣는다. 앞 턴의 파일은 여기 오지 않는다.
          files,
        } as any,
        onProgress: (step) => onProgress(step as AgentProgress),
        // 첫 질문은 계획을 과제와 함께 미리 시작한다 — 과제가 같은 스킬을 고르면 그대로 쓴다(런타임 `speculatePlan`).
        speculate: true,
      });
      // 근거를 답변 끝에 붙인다. 어느 화면을 무슨 조건으로 봤는지 없으면 검증할 수 없다.
      const sources = answer.sources.length
        ? `\n\n조회한 화면: ${answer.sources.join(', ')}`
        : '';
      // 경로만 따로 넘긴다. 답변 문장에서 다시 긁어내면 문구가 바뀔 때 조용히 깨진다.
      // 조회 중에 앱이 띄운 알림. 사용자는 패널을 보고 있어서 못 봤다.
      const notices = answer.notices?.length
        ? `

조회 중 알림: ${answer.notices.join(' / ')}`
        : '';
      const routes = plan_routes(answer);
      onProgress({
        kind: 'done', text: `${answer.text}${notices}${sources}`, table: answer.table, routes,
        experience: answer.experience, formats: answer.formats,
        result: answer.result, report: answer.report, task: answer.task,
        ...(answer.queued?.length ? { queued: answer.queued } : {}),
        // 답 아래 선택 단추(판정이 어긋나 기본 규칙대로 뺐을 때의 「넣어서 다시」).
        ...(answer.options?.length ? { options: answer.options } : {}),
      });
    } catch (error) {
      // 되묻기는 실패가 아니다. 사용자 차례라는 뜻이라 오류로 보이면 안 된다.
      if ((error as Error)?.name === 'AskUser') {
        // 되묻기는 끝난 턴이 아니지만 **하던 작업은 살아 있다.** 여기서 안 실어
        // 보내면 다음 턴이 `워드로 만들어줘` 를 잊은 채 시작한다 — 답은 나오는데
        // 파일 버튼이 없다.
        const asking = error as Error & { question: string; options?: string[]; task?: TaskSpec };
        onProgress({
          kind: 'ask', question: asking.question, options: asking.options, task: asking.task,
        });
        return;
      }
      throw error;
    } finally {
      sandbox.close();
    }
  };

  (window as unknown as { __elmanAgentAsk?: AgentAsk }).__elmanAgentAsk = run;
  // 회사가 공유하는 이름 짝. 읽기와 배우기가 한 호출이다 — 배운 것을 올리면 합쳐진
  // 전체를 돌려준다.
  window.__elmanAgentAliases = async (learned, forget) => {
    const who = caller();
    if (!who) return {};
    return makeAliases(who)(learned, forget);
  };
  window.__elmanAgentPromote = async (candidate) => {
    const who = caller();
    if (!who) return;
    await makeExperiencePromote(who)(candidate);
  };
  /**
   * **검색 서버가 고른 후보만 돌려준다.** 계획 모델은 안 부른다.
   *
   * 왜 필요한가 — 「고르기에서 모델을 뺀다」를 검토하려면 **검색 1등이 얼마나 맞는지**를
   * 알아야 하는데, 한 번도 잰 적이 없다. 재려면 화면 고르기까지만 돌려야 하고, 지금은
   * 질문 하나에 계획 호출이 통째로 붙어 하루 AI 한도를 태운다.
   *
   * 자격증명은 안 나간다 — 경로 목록만 돌려준다.
   */
  (window as unknown as {
    __elmanAgentShortlist?: (question: string) => Promise<string[]>;
  }).__elmanAgentShortlist = async (question: string) => {
    const who = caller();
    if (!who) throw new Error('로그인 정보가 없습니다.');
    return makeShortlist(who)(question);
  };
}

